import { useQueries, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import {
  FetchCampaignCreatorCount,
  FetchCampaignCreators,
  type FetchCampaignCreatorCountsRequest,
  type FetchCampaignCreatorsRequest,
} from '..';

type QueryOptions = {
  enabled?: boolean;
  pagination?: {
    pageSize: number;
    resetKey?: readonly unknown[];
  };
};

export function useCampaignCreators(
  campaignId: string,
  pending: boolean,
  request?: FetchCampaignCreatorsRequest,
  options?: QueryOptions,
) {
  const queryKeys = useQueryKeys();
  const [page, setPage] = useState(1);
  const pageSize = options?.pagination?.pageSize;
  const resetKey = options?.pagination?.resetKey;

  useEffect(() => {
    if (!resetKey) return;

    setPage(1);
  }, [resetKey]);

  const campaignCreatorRequest = useMemo<FetchCampaignCreatorsRequest | undefined>(() => {
    if (!pageSize) return request;

    return {
      ...request,
      limit: pageSize,
      page,
    };
  }, [page, pageSize, request]);

  const query = useQuery({
    enabled: campaignId.length > 0 && (options?.enabled ?? true),
    queryKey: queryKeys.campaign.creators(campaignId, pending, campaignCreatorRequest),
    queryFn: ({ signal }) =>
      FetchCampaignCreators(campaignId, pending, campaignCreatorRequest, signal),
    staleTime: 30_000,
  });

  const totalCount = query.data?.pagination.total ?? 0;
  const resolvedPageSize = pageSize ?? query.data?.pagination.limit ?? 0;
  const totalPages =
    resolvedPageSize > 0 ? Math.max(1, Math.ceil(totalCount / resolvedPageSize)) : 1;
  const pageStartIndex = resolvedPageSize > 0 ? (page - 1) * resolvedPageSize : 0;
  const startIndex = totalCount > 0 ? pageStartIndex + 1 : 0;
  const endIndex = Math.min(pageStartIndex + (query.data?.items.length ?? 0), totalCount);

  const goToPreviousPage = () => {
    setPage((currentPage) => Math.max(currentPage - 1, 1));
  };

  const goToNextPage = () => {
    setPage((currentPage) => Math.min(currentPage + 1, totalPages));
  };

  return {
    ...query,
    pagination: {
      canGoNext: page < totalPages,
      canGoPrevious: page > 1,
      endIndex,
      goToNextPage,
      goToPreviousPage,
      page,
      pageSize: resolvedPageSize,
      pageStartIndex,
      setPage,
      shouldPaginate: resolvedPageSize > 0 && totalCount > resolvedPageSize,
      startIndex,
      totalCount,
      totalPages,
    },
  };
}

export function useCampaignCreatorCounts(
  campaignId: string,
  request?: FetchCampaignCreatorCountsRequest,
  options?: { enabled?: boolean },
) {
  const queryKeys = useQueryKeys();
  const enabled = campaignId.length > 0 && (options?.enabled ?? true);
  const [totalQuery, pendingQuery] = useQueries({
    queries: [
      {
        enabled,
        queryKey: queryKeys.campaign.creatorCount(campaignId, false, request),
        queryFn: ({ signal }) => FetchCampaignCreatorCount(campaignId, false, request, signal),
        staleTime: 30_000,
      },
      {
        enabled,
        queryKey: queryKeys.campaign.creatorCount(campaignId, true, request),
        queryFn: ({ signal }) => FetchCampaignCreatorCount(campaignId, true, request, signal),
        staleTime: 30_000,
      },
    ],
  });
  const total = totalQuery.data ?? 0;
  const pending = pendingQuery.data ?? 0;
  const hasAnyData = totalQuery.data != null || pendingQuery.data != null;

  return {
    data: hasAnyData ? { total, pending } : undefined,
    error: totalQuery.error ?? pendingQuery.error ?? null,
    isError: totalQuery.isError || pendingQuery.isError,
    isFetching: totalQuery.isFetching || pendingQuery.isFetching,
    isLoading: totalQuery.isLoading || pendingQuery.isLoading,
    pending,
    pendingQuery,
    refetch: () => Promise.all([totalQuery.refetch(), pendingQuery.refetch()]),
    total,
    totalQuery,
  };
}
